import { memo, createContext, useContext, useState, useEffect, useRef } from 'react'
import { Handle, Position, NodeResizer, useReactFlow, type NodeProps, type Node } from '@xyflow/react'
import {
  Router, Network, BrickWall, Cloud, Globe, Server, ServerCog, HardDrive, Database, Wifi, RadioTower,
  Monitor, Laptop, Printer, Cctv, Phone, Smartphone, BatteryCharging, Cable, ShieldCheck, Box, Cpu,
  type LucideIcon,
} from 'lucide-react'

// ─── Device catalog ──────────────────────────────────────────────────────────
export interface DeviceKind { kind: string; label: string; icon: LucideIcon; color: string }

export const DEVICE_KINDS: DeviceKind[] = [
  { kind: 'cloud',    label: 'Cloud / ISP',      icon: Cloud,           color: '#0ea5e9' },
  { kind: 'internet', label: 'Internet',         icon: Globe,           color: '#0284c7' },
  { kind: 'router',   label: 'Router',           icon: Router,          color: '#2563eb' },
  { kind: 'firewall', label: 'Firewall',         icon: BrickWall,       color: '#dc2626' },
  { kind: 'l3switch', label: 'Core / L3 Switch', icon: Network,         color: '#7c3aed' },
  { kind: 'switch',   label: 'Switch',           icon: Network,         color: '#0891b2' },
  { kind: 'ap',       label: 'Access Point',     icon: Wifi,            color: '#16a34a' },
  { kind: 'wireless', label: 'Wireless Bridge',  icon: RadioTower,      color: '#15803d' },
  { kind: 'server',   label: 'Server',           icon: Server,          color: '#475569' },
  { kind: 'hypervisor', label: 'Hypervisor / VM', icon: ServerCog,      color: '#334155' },
  { kind: 'storage',  label: 'NAS / Storage',    icon: HardDrive,       color: '#6b7280' },
  { kind: 'database', label: 'Database',         icon: Database,        color: '#9333ea' },
  { kind: 'vpn',      label: 'VPN / Security',   icon: ShieldCheck,     color: '#ea580c' },
  { kind: 'pc',       label: 'PC / Desktop',     icon: Monitor,         color: '#4b5563' },
  { kind: 'laptop',   label: 'Laptop',           icon: Laptop,          color: '#4b5563' },
  { kind: 'printer',  label: 'Printer',          icon: Printer,         color: '#64748b' },
  { kind: 'cctv',     label: 'CCTV / NVR',       icon: Cctv,            color: '#c2410c' },
  { kind: 'phone',    label: 'IP Phone',         icon: Phone,           color: '#0f766e' },
  { kind: 'mobile',   label: 'Mobile',           icon: Smartphone,      color: '#0f766e' },
  { kind: 'ups',      label: 'UPS',              icon: BatteryCharging, color: '#ca8a04' },
  { kind: 'patch',    label: 'Patch Panel',      icon: Cable,           color: '#78716c' },
  { kind: 'iot',      label: 'IoT / Controller', icon: Cpu,             color: '#a16207' },
  { kind: 'generic',  label: 'อุปกรณ์อื่นๆ',      icon: Box,             color: '#6b7280' },
]
export const KIND_MAP: Record<string, DeviceKind> = Object.fromEntries(DEVICE_KINDS.map(k => [k.kind, k]))

// Best-effort mapping of an asset's type/category/name to a diagram device kind
export function guessKind(a: { type?: string | null; category?: string | null; name?: string | null }): string {
  const s = `${a.type || ''} ${a.category || ''} ${a.name || ''}`.toLowerCase()
  const rules: [RegExp, string][] = [
    [/firewall|fortigate|sophos|palo ?alto|utm/, 'firewall'],
    [/router|mikrotik|gateway/, 'router'],
    [/core|l3 ?switch|layer ?3/, 'l3switch'],
    [/switch/, 'switch'],
    [/access ?point|\bap\b|wifi|wi-fi|wireless|unifi/, 'ap'],
    [/nas|storage|san\b|synology|qnap/, 'storage'],
    [/esxi|hyper-?v|vmware|proxmox|hypervisor|\bvm\b/, 'hypervisor'],
    [/server/, 'server'],
    [/printer|เครื่องพิมพ์|copier/, 'printer'],
    [/cctv|camera|nvr|dvr|กล้อง/, 'cctv'],
    [/laptop|notebook|โน้ตบุ๊ก/, 'laptop'],
    [/desktop|\bpc\b|computer|คอมพิวเตอร์|all ?in ?one|workstation/, 'pc'],
    [/ip ?phone|telephone|โทรศัพท์/, 'phone'],
    [/mobile|smartphone|tablet|ipad|iphone/, 'mobile'],
    [/\bups\b/, 'ups'],
    [/patch/, 'patch'],
  ]
  for (const [re, k] of rules) if (re.test(s)) return k
  return 'generic'
}

// ─── Node data shapes ────────────────────────────────────────────────────────
export type DeviceData = {
  kind: string
  label?: string
  sublabel?: string
  ip?: string
  details?: string
  color?: string
  labelColor?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  image?: string | null
  imageFit?: 'contain' | 'cover'
  shape?: 'tile' | 'circle' | 'plain'
  labelPos?: 'bottom' | 'right'
  assetId?: string | null
  assetCode?: string | null
  showAssetCode?: boolean
  // Custom box width in px (set by dragging the resize corner). Images keep their aspect ratio.
  w?: number
}
export type ZoneData = {
  label?: string
  fill?: string
  border?: string
  borderStyle?: 'solid' | 'dashed' | 'dotted' | 'none'
  borderWidth?: number
  opacity?: number
  radius?: number
  shape?: 'rect' | 'ellipse'
  fontSize?: number
  labelColor?: string
  labelPos?: 'top-left' | 'top-center' | 'bottom-left' | 'bottom-right' | 'center'
}
export type TextData = {
  text?: string
  fontSize?: number
  color?: string
  bold?: boolean
  italic?: boolean
  bg?: string
  align?: 'left' | 'center' | 'right'
}

export type DeviceNodeT = Node<DeviceData, 'device'>
export type ZoneNodeT = Node<ZoneData, 'zone'>
export type TextNodeT = Node<TextData, 'text'>
export type DiagramNode = DeviceNodeT | ZoneNodeT | TextNodeT

// Whether the canvas is in edit mode — node renderers show handles/resizers only when editing
export const EditModeContext = createContext(false)
// Lets node renderers tell the editor a change gesture is starting (for undo snapshots)
export const DiagramActionsContext = createContext<{ beginChange: () => void }>({ beginChange: () => {} })

// Default box width for a device: framed icons are square; a frameless image defaults wider
// (rack gear photos are wide) and its height follows the picture's own aspect ratio.
export function deviceBoxWidth(d: DeviceData): number {
  if (d.w) return d.w
  const px = SIZE_PX[d.size || 'md']
  return d.image && (d.shape || 'tile') === 'plain' ? Math.round(px * 3) : px
}

const SIZE_PX = { sm: 36, md: 48, lg: 64, xl: 84, '2xl': 120 }

const SIDES: { id: string; pos: Position }[] = [
  { id: 't', pos: Position.Top }, { id: 'r', pos: Position.Right },
  { id: 'b', pos: Position.Bottom }, { id: 'l', pos: Position.Left },
]

function Handles() {
  // ConnectionMode.Loose lets any of these source handles connect to any other
  return <>{SIDES.map(s => <Handle key={s.id} id={s.id} type="source" position={s.pos} className="nd-handle"/>)}</>
}

function ResizeCorner({ id, width }: { id: string; width: number }) {
  const rf = useReactFlow()
  const { beginChange } = useContext(DiagramActionsContext)
  const drag = useRef<{ x: number; w: number } | null>(null)
  return (
    <div className="nodrag nopan nd-resize" title="ลากเพื่อปรับขนาด"
      onPointerDown={e => {
        e.stopPropagation()
        beginChange()
        drag.current = { x: e.clientX, w: width }
        ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
      }}
      onPointerMove={e => {
        if (!drag.current) return
        const w = Math.round(Math.max(20, Math.min(1600, drag.current.w + (e.clientX - drag.current.x) / rf.getZoom())))
        rf.updateNodeData(id, { w })
      }}
      onPointerUp={e => { drag.current = null; (e.target as HTMLElement).releasePointerCapture(e.pointerId) }}
    />
  )
}

export const DeviceNode = memo(({ id, data, selected }: NodeProps<DeviceNodeT>) => {
  const editing = useContext(EditModeContext)
  const k = KIND_MAP[data.kind] || KIND_MAP.generic
  const Icon = k.icon
  const color = data.color || k.color
  const shape = data.shape || 'tile'
  const right = data.labelPos === 'right'
  // Fall back to the kind icon if the image URL is broken (e.g. the asset photo was deleted)
  const [imgBroken, setImgBroken] = useState(false)
  useEffect(() => setImgBroken(false), [data.image])
  const useImage = !!data.image && !imgBroken
  const freeImage = useImage && shape === 'plain'
  const w = deviceBoxWidth(useImage ? data : { ...data, image: null })
  const radius = shape === 'circle' ? '50%' : Math.round(w * 0.18)
  const iconBox: React.CSSProperties = freeImage
    ? { width: w }
    : useImage
    ? { width: w, height: w, background: '#fff', borderRadius: radius, border: `2px solid ${color}`, boxShadow: '0 2px 6px rgba(0,0,0,.2)', overflow: 'hidden' }
    : shape === 'plain'
    ? { color, width: w, height: w }
    : {
        width: w, height: w,
        background: `linear-gradient(160deg, ${color} 0%, ${shade(color, -0.25)} 100%)`,
        color: '#fff',
        borderRadius: radius,
        boxShadow: '0 2px 6px rgba(0,0,0,.25), inset 0 1px 0 rgba(255,255,255,.35)',
      }
  return (
    <div className={`nd-device ${selected ? 'nd-selected' : ''} ${editing ? 'nd-editing' : ''}`}
      style={{ display: 'flex', flexDirection: right ? 'row' : 'column', alignItems: 'center', gap: right ? 8 : 3 }}>
      <div style={{ position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center', ...iconBox }}>
        {useImage
          ? <img src={data.image!} alt="" draggable={false} onError={() => setImgBroken(true)}
              style={freeImage
                ? { width: '100%', height: 'auto', display: 'block', pointerEvents: 'none' }
                : { width: '100%', height: '100%', objectFit: data.imageFit || 'contain', padding: data.imageFit === 'cover' ? 0 : 3, pointerEvents: 'none' }}/>
          : <Icon size={Math.round(w * (shape === 'plain' ? 0.95 : 0.6))} strokeWidth={shape === 'plain' ? 1.6 : 1.9}/>}
        <Handles/>
        {editing && selected && <ResizeCorner id={id} width={w}/>}
      </div>
      {(data.label || data.ip || data.sublabel || data.details || (data.assetCode && data.showAssetCode !== false)) && (
        <div style={{ textAlign: right ? 'left' : 'center', lineHeight: 1.2, maxWidth: right ? 240 : Math.max(200, w), color: data.labelColor || '#111827' }}>
          {data.label && <div style={{ fontSize: 12, fontWeight: 600, whiteSpace: 'pre-wrap' }}>{data.label}</div>}
          {data.sublabel && <div style={{ fontSize: 10.5, opacity: .75, whiteSpace: 'pre-wrap' }}>{data.sublabel}</div>}
          {data.ip && <div style={{ fontSize: 10.5, fontFamily: 'ui-monospace, monospace', color: '#1d4ed8' }}>{data.ip}</div>}
          {data.details && <div style={{ fontSize: 10, opacity: .7, whiteSpace: 'pre-wrap' }}>{data.details}</div>}
          {data.assetCode && data.showAssetCode !== false && (
            <div style={{ display: 'inline-block', marginTop: 2, fontSize: 9.5, fontWeight: 600, padding: '0 5px', borderRadius: 4, background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}>
              {data.assetCode}
            </div>
          )}
        </div>
      )}
    </div>
  )
})

export const ZoneNode = memo(({ data, selected }: NodeProps<ZoneNodeT>) => {
  const editing = useContext(EditModeContext)
  const pos = data.labelPos || 'bottom-left'
  const labelStyle: React.CSSProperties = {
    position: 'absolute', padding: '6px 10px', fontWeight: 700, letterSpacing: .3,
    fontSize: data.fontSize || 16, color: data.labelColor || '#57534e', pointerEvents: 'none', whiteSpace: 'nowrap',
    ...(pos === 'top-left' && { top: 0, left: 0 }),
    ...(pos === 'top-center' && { top: 0, left: '50%', transform: 'translateX(-50%)' }),
    ...(pos === 'bottom-left' && { bottom: 0, left: 0 }),
    ...(pos === 'bottom-right' && { bottom: 0, right: 0 }),
    ...(pos === 'center' && { top: '50%', left: '50%', transform: 'translate(-50%,-50%)' }),
  }
  return (
    <>
      <NodeResizer isVisible={editing && !!selected} minWidth={60} minHeight={40} color="#2563eb"/>
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div style={{
          position: 'absolute', inset: 0,
          background: data.fill ?? '#fde68a', opacity: data.opacity ?? 0.55,
          borderRadius: data.shape === 'ellipse' ? '50%' : (data.radius ?? 16),
        }}/>
        <div style={{
          position: 'absolute', inset: 0,
          border: data.borderStyle === 'none' ? 'none' : `${data.borderWidth ?? 1.5}px ${data.borderStyle || 'dashed'} ${data.border || '#78716c'}`,
          borderRadius: data.shape === 'ellipse' ? '50%' : (data.radius ?? 16),
          boxShadow: selected ? '0 0 0 2px #3b82f6' : undefined,
        }}/>
        {data.label && <div style={labelStyle}>{data.label}</div>}
      </div>
    </>
  )
})

export const TextNode = memo(({ data, selected }: NodeProps<TextNodeT>) => (
  <div style={{
    fontSize: data.fontSize || 14, color: data.color || '#111827',
    fontWeight: data.bold ? 700 : 400, fontStyle: data.italic ? 'italic' : 'normal',
    background: data.bg || 'transparent', padding: data.bg ? '4px 8px' : 2, borderRadius: 4,
    textAlign: data.align || 'left', whiteSpace: 'pre-wrap', minWidth: 20,
    outline: selected ? '2px solid #3b82f6' : undefined, outlineOffset: 2,
  }}>
    {data.text || 'ข้อความ'}
  </div>
))

export const nodeTypes = { device: DeviceNode, zone: ZoneNode, text: TextNode }

// Darken (amt < 0) or lighten (amt > 0) a #rrggbb color
export function shade(hex: string, amt: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return hex
  const n = parseInt(m[1], 16)
  const f = (c: number) => Math.max(0, Math.min(255, Math.round(amt < 0 ? c * (1 + amt) : c + (255 - c) * amt)))
  const r = f(n >> 16), g = f((n >> 8) & 255), b = f(n & 255)
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, '0')}`
}
